import { localizeRoute } from '../../utils/constants/url-constants';

export type PrimaryNavLocale = 'en' | 'fi' | 'sv';

export interface PrimaryNavLink {
  key: string;
  /** Logical (EN) path; localized per locale via `localizeRoute` + locale prefix. */
  logicalPath: string;
  labels: Record<PrimaryNavLocale, string>;
}

/**
 * Single source of truth for the primary navbar links shown on every non-SRP page.
 * Both renderers consume this list — the hydrated `NavigationNavbar` (web-app pages)
 * and the static `ResourceNavbar` twin (prerendered web-static pages) — so their
 * link sets cannot drift apart. Labels are carried per-locale here (not react-intl)
 * because the static twin renders without an IntlProvider.
 */
export const PRIMARY_NAV_LINKS: PrimaryNavLink[] = [
  { key: 'home', logicalPath: '/', labels: { en: 'Home', fi: 'Etusivu', sv: 'Hem' } },
  { key: 'jobs', logicalPath: '/jobs', labels: { en: 'Search Jobs', fi: 'Hae työpaikkoja', sv: 'Sök jobb' } },
  { key: 'resources', logicalPath: '/resources', labels: { en: 'Resources', fi: 'Työelämä', sv: 'Arbetsliv' } },
  { key: 'locations', logicalPath: '/locations', labels: { en: 'Locations', fi: 'Toimipisteet', sv: 'Platser' } },
  { key: 'companies', logicalPath: '/best-companies', labels: { en: 'Companies', fi: 'Yritykset', sv: 'Företag' } },
  { key: 'roles', logicalPath: '/salary', labels: { en: 'Roles', fi: 'Roolit', sv: 'Roller' } },
];

/** Fully-localized href (locale prefix + localized segment) for static `<a>` rendering. */
export function primaryNavHref(link: PrimaryNavLink, locale: PrimaryNavLocale): string {
  if (link.logicalPath === '/') return `/${locale}`;
  return `/${locale}${localizeRoute(link.logicalPath, locale)}`;
}

export function primaryNavLabel(link: PrimaryNavLink, locale: string): string {
  return link.labels[(locale as PrimaryNavLocale)] ?? link.labels.en;
}

/** Localized href for a link by its `key`; throws on unknown keys so typos fail fast in tests. */
export function primaryNavHrefByKey(key: string, locale: PrimaryNavLocale): string {
  const link = PRIMARY_NAV_LINKS.find((l) => l.key === key);
  if (!link) throw new Error(`Unknown primary nav link key: ${key}`);
  return primaryNavHref(link, locale);
}
